import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import Card from '../common/Card';
import { getCssVar } from '../../utils/cssVar';
import './SerpFeaturesChart.css';

const FEATURE_LABELS = {
  organic: 'Organiques',
  generative_trigger: 'AIOverviews',
  related_search: 'Recherches associées',
  people_also_ask: 'People Also Ask',
  shopping: 'Shopping',
  image_pack: 'Images',
  images: 'Images',
  video: 'Vidéos',
  videos: 'Vidéos',
  local_pack: 'Pack local',
  knowledge_panel: 'Knowledge Panel',
  news: 'Actualités',
  featured_snippet: 'Featured Snippet',
  twitter: 'Twitter / X',
  top_stories: 'Top Stories',
  jobs: 'Offres d\'emploi',
  recipes: 'Recettes',
  flights: 'Vols',
  hotels: 'Hôtels',
  sports: 'Sports',
  maps: 'Maps',
  scholarly_articles: 'Articles académiques',
  podcast: 'Podcast',
  apps: 'Applications',
  books: 'Livres',
  finance: 'Finance',
  events: 'Événements',
};

// Ordre : verts principaux (organiques/images/videos) -> verts derives (local/news) -> couleurs secondaires
const FEATURE_COLOR_VARS = {
  // Features SERP principales - palette verte
  organic: '--chart-primary-2',
  images: '--chart-primary-3',
  image_pack: '--chart-primary-3',
  videos: '--chart-primary-1',
  video: '--chart-primary-1',
  local_pack: '--chart-primary-4',
  news: '--chart-secondary-vert',
  featured_snippet: '--chart-secondary-vert-mid',
  // Features secondaires - autres couleurs
  generative_trigger: '--chart-secondary-violet',
  shopping: '--chart-secondary-jaune',
  maps: '--chart-secondary-jaune-mid',
  people_also_ask: '--color-gray-500',
  related_search: '--chart-secondary-bleu',
  top_stories: '--chart-secondary-bleu-mid',
  knowledge_panel: '--chart-secondary-violet',
  scholarly_articles: '--chart-secondary-violet-mid',
  twitter: '--chart-secondary-bleu',
  jobs: '--chart-secondary-beige',
  recipes: '--chart-secondary-vert-mid',
  flights: '--chart-secondary-bleu-mid',
  hotels: '--chart-secondary-rose',
  sports: '--chart-secondary-vert',
  podcast: '--chart-secondary-rose-mid',
  apps: '--chart-secondary-beige-mid',
  books: '--chart-secondary-beige',
  finance: '--chart-secondary-vert-mid',
  events: '--color-error',
};

// Ordre fallback : verts d'abord, puis autres couleurs secondaires
const FALLBACK_COLOR_VARS = [
  '--chart-primary-2',
  '--chart-primary-3',
  '--chart-secondary-vert',
  '--chart-primary-4',
  '--chart-secondary-vert-mid',
  '--chart-secondary-bleu',
  '--chart-secondary-violet',
  '--chart-secondary-jaune',
  '--chart-secondary-beige',
  '--chart-secondary-rose',
];

const getFeatureLabel = (type) => FEATURE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatCount = (count) => {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

const SerpFeaturesChart = ({ data }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const featureColors = useMemo(() => {
    const resolved = {};
    Object.entries(FEATURE_COLOR_VARS).forEach(([key, varName]) => {
      resolved[key] = getCssVar(varName);
    });
    return resolved;
  }, []);

  const fallbackColors = useMemo(() => FALLBACK_COLOR_VARS.map(getCssVar), []);
  const centerDefaultColor = useMemo(() => getCssVar('--color-gray-900'), []);

  if (!data || data.length === 0) {
    return (
      <Card className="serp-features-chart">
        <h3 className="chart-title">Types de résultats SERP</h3>
        <p className="chart-empty">Aucune donnée disponible</p>
      </Card>
    );
  }

  const getFeatureColor = (type, fallbackIndex) =>
    featureColors[type] || fallbackColors[fallbackIndex % fallbackColors.length];

  const segments = data.map((item, index) => ({
    ...item,
    value: item.percentage,
    color: getFeatureColor(item.featureType, index),
    label: getFeatureLabel(item.featureType),
  }));

  const centerLabel = hoveredIndex !== null ? segments[hoveredIndex] : null;

  return (
    <Card className="serp-features-chart">
      <h3 className="chart-title">Types de résultats SERP</h3>
      <div className="serp-features-content">

        <div className="donut-wrapper">
          <ResponsiveContainer width={220} height={220}>
            <PieChart>
              <Pie
                data={segments}
                cx="50%"
                cy="50%"
                innerRadius={65}
                outerRadius={90}
                dataKey="value"
                paddingAngle={2}
                startAngle={90}
                endAngle={-270}
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {segments.map((seg, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={seg.color}
                    opacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.4}
                    stroke="none"
                    outerRadius={hoveredIndex === index ? 95 : 90}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="donut-center-label-box">
            {centerLabel ? (
              <>
                <span className="donut-center-pct" style={{ color: centerLabel.color }}>
                  {centerLabel.percentage.toFixed(1)}%
                </span>
                <span className="donut-center-name">{centerLabel.label}</span>
              </>
            ) : (
              <>
                <span className="donut-center-pct" style={{ color: centerDefaultColor }}>{segments.length}</span>
                <span className="donut-center-name">types</span>
              </>
            )}
          </div>
        </div>

        <div className="donut-legend">
          {segments.map((seg, index) => (
            <div
              key={`legend-${seg.featureType}-${index}`}
              className={`donut-legend-item${hoveredIndex === index ? ' donut-legend-item--hovered' : ''}`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <span className="donut-legend-dot" style={{ backgroundColor: seg.color }} />
              <span className="donut-legend-name">{seg.label}</span>
              <span className="donut-legend-pct">{seg.percentage.toFixed(2)}%</span>
              <span className="donut-legend-count">{formatCount(seg.count)}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default SerpFeaturesChart;
